import React, { useState, createRef, useCallback } from "react";
import ReactDOM from 'react-dom';
import { Link, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, IconButton, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import { updateProspect } from 'api/prospector/prospects';
import { GetUnderwrite } from "api/revue/underwriteData";
// import { compose } from 'redux';

import { Constants } from 'utilities/constants';
import { userRoleSourcingUserSelector, userRoleSourcingManagerSelector } from 'store/selectors';
import PropertyDetailsPage from "components/explore/propertyDetailsPage";
import RejectModal from 'components/explore/prospector/RejectModal';
import { LoadingOverlayWrapper } from 'components/layout';
import CreateUnderwriteModal from 'components/explore/prospector/CreateUnderwriteModal';
import { LocationVerificationModal } from 'components/underwrite/acquisition/import/importOpportunities/LocationVerification';
import ProspectsGrid from 'components/explore/prospector/ProspectsGrid';
import UnderwriteInfoModal from 'components/explore/prospector/UnderwriteInfoModal';
import { getPropertyDetailsDataForProspect, appNotify } from 'store/actions';

const styles = theme => ({
  gridPaper: {
    padding: '5px 8px 8px',
    // overflow: 'hidden',
    width: '97%',
    height: 'inherit'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // width: '66%'
    height: '95%'
  },
  title: {
    margin: '15px'
  },
  overlay: {
    // height: 'calc(100vh - (100vh - 100%) - 48px)',
    height: '100vh',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    maxWidth: '100%'
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  titleContentContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
});

const prospectsGridRef = createRef();

const Prospects = ({
  classes,
  setProspectorModalOpen,
  userHasSourcingUserRole,
  userHasSourcingManagerRole,
  user,
  refreshProspectsGrid,
  getPropertyDetailsDataForProspect,
  appNotify,
  setRefreshProspectsGrid
}) => {
  const [underwriteTxnOppId, setUnderwriteTxnOppId] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [modalSelectedRows, setModalSelectedRows] = useState([]);
  const [isCreateUnderwriteModalOpen, setIsCreateUnderwriteModalOpen] = useState(false);
  const [openLocationVerification, setOpenLocationVerification] = useState(false);
  const [addressToVerify, setAddressToVerify] = useState(null);
  const [propertyDetailsModalOpen, setPropertyDetailsModalOpen] = useState(false);
  const [underwriteInfoModalOpen, setUnderwriteInfoModalOpen] = useState(false);
  const [gridLoading, setIsProspectsLoading] = useState({ loading: false, message: '' });

  const refreshButtonClick = () => {
    setRefreshProspectsGrid(refreshProspectsGrid + 1);
  }

  const updateLocation = async (geoResultDto) => {
    const row = modalSelectedRows[0];
    const updatedProspect = {
      asgPropID: geoResultDto.propertyMasterResult.asgPropID || row.asgPropID,
      id: row.id,
      beds: row.beds,
      baths: row.baths,
      sqft: row.sqft,
      yearBuilt: row.yearBuilt,
      inspectionPeriod: row.inspectionPeriod,
      strategy: row.strategy,
      opportunitySource: row.opportunitySource,
      listingPrice: row.listingPrice,
      notes: row.notes,
      streetAddress: geoResultDto.propertyMasterResult.cleanAddress,
      city: geoResultDto.propertyMasterResult.city,
      state: geoResultDto.propertyMasterResult.state,
      zipCode: geoResultDto.propertyMasterResult.zip,
      latitude: geoResultDto.propertyMasterResult.latitude,
      longitude: geoResultDto.propertyMasterResult.longitude,
      locationVerified: true
    };
    await updateProspect(updatedProspect, user.id);
    setAddressToVerify(null);
    await refreshButtonClick();
  }

  const viewPropertyDetails = useCallback(e => {
    ReactDOM.unstable_batchedUpdates(() => {
      setIsProspectsLoading({ loading: true, message: 'Loading Property Details' });
      setModalSelectedRows([e.row.data]);
      getPropertyDetailsDataForProspect({
        asgPropID: e.row.data.asgPropID,
        callback: (shouldOpen) => {
          setIsProspectsLoading({ loading: false, message: '' });
          if (shouldOpen) setPropertyDetailsModalOpen(true);
        }
      });
    });
  }, [getPropertyDetailsDataForProspect]);

  const onPropertyDetailClose = () => {
    setPropertyDetailsModalOpen(false);
  }

  const handleLocationVerificationClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
    setOpenLocationVerification(false);
  }

  return (
    <LoadingOverlayWrapper
      className={classes.overlay}
      active={gridLoading.loading}
      message={gridLoading.message}
      zIndex={10000}
    >
      <div className={classes.container}>
        <div className={classes.titleContainer}>
          <div className={classes.titleContentContainer}>
            <Tooltip title='Add Prospect'>
              <span>
                <Link to={{
                  pathname: Constants.Routes.EXPLORE_PROSPECTOR,
                  state: { modal: true }
                }}>
                  <IconButton disabled={gridLoading.loading} onClick={() => setProspectorModalOpen(true)}>
                    <AddCircleIcon />
                  </IconButton>
                </Link>
              </span>
            </Tooltip>
            <Typography variant='h5' className={classes.title}>Prospects</Typography>
            <Tooltip title='Refresh Data'>
              <span>
                <IconButton disabled={gridLoading.loading} onClick={refreshButtonClick} className={classes.refreshButton}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <Paper className={classes.gridPaper}>
          <ProspectsGrid
            prospectsGridRef={prospectsGridRef}
            setIsRejectModalOpen={setIsRejectModalOpen}
            setModalSelectedRows={setModalSelectedRows}
            setGridLoading={setIsProspectsLoading}
            setIsCreateUnderwriteModalOpen={setIsCreateUnderwriteModalOpen}
            viewPropertyDetails={viewPropertyDetails}
            setAddressToVerify={setAddressToVerify}
            setOpenLocationVerification={setOpenLocationVerification}
            user={user}
            userHasSourcingUserRole={userHasSourcingUserRole}
            userHasSourcingManagerRole={userHasSourcingManagerRole}
            setUnderwriteInfoModalOpen={setUnderwriteInfoModalOpen}
            setUnderwriteTxnOppId={setUnderwriteTxnOppId}
            appNotify={appNotify}
            refreshProspectsGrid={refreshProspectsGrid}
            loading={gridLoading.loading}
          />
          <PropertyDetailsPage
            onClose={onPropertyDetailClose} prospectProperty={modalSelectedRows[0]}
            prospectsPropertyDetailsIsOpen={propertyDetailsModalOpen} />
          <UnderwriteInfoModal
            selectedRow={modalSelectedRows[0]}
            isOpen={underwriteInfoModalOpen}
            setIsOpen={setUnderwriteInfoModalOpen}
            getUnderwriteInfo={GetUnderwrite}
            underwriteTxnOppId={underwriteTxnOppId}
            setUnderwriteTxnOppId={setUnderwriteTxnOppId}
            setGridLoading={setIsProspectsLoading} />
          <RejectModal
            selectedRows={modalSelectedRows}
            isOpen={isRejectModalOpen}
            setIsOpen={setIsRejectModalOpen}
            refreshButtonClick={refreshButtonClick}
          />
          <CreateUnderwriteModal
            selectedRows={modalSelectedRows}
            isOpen={isCreateUnderwriteModalOpen}
            setIsOpen={setIsCreateUnderwriteModalOpen}
            refreshButtonClick={refreshButtonClick}
          />
          {prospectsGridRef.current && openLocationVerification ?
            <LocationVerificationModal open={openLocationVerification} onClose={handleLocationVerificationClose} geoValidationDto={addressToVerify} updateLocation={updateLocation} jitCreationType='prospects' /> : null
          }
        </Paper>
      </div>
    </LoadingOverlayWrapper>
  )
}

const mapState = state => {
  return {
    userHasSourcingUserRole: userRoleSourcingUserSelector(state),
    userHasSourcingManagerRole: userRoleSourcingManagerSelector(state),
    user: state.appState.user
  }
};

const mapDispatch = {
  appNotify,
  getPropertyDetailsDataForProspect,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapState,
    mapDispatch
  )
)(Prospects);
